import { useState, useEffect, useRef } from 'react';

/**
 * Searchable dropdown — drop-in replacement for <select>.
 *
 * Props:
 *   value       — selected id (string/number) or ''
 *   onChange    — (id: string) => void
 *   options     — [{ value: string|number, label: string, disabled?: boolean }]
 *   placeholder — shown when nothing selected (default "Seç...")
 *   disabled    — grays out, prevents interaction
 *   className   — extra classes on the wrapper div
 *   keepOpen    — "add mode": seçəndən sonra bağlanmır, axtarışı təmizləyib yenidən
 *                 fokuslayır (ardıcıl əlavə üçün). Klaviatura: ↑/↓ gəz, Enter seç, Esc bağla.
 *   apiRef      — (istəyə bağlı) ref: apiRef.current.focus() → axtarışa fokus + siyahını aç
 */
export default function SearchSelect({
    value,
    onChange,
    options = [],
    placeholder = 'Seç...',
    disabled = false,
    className = '',
    keepOpen = false,
    apiRef = null,
}) {
    const [search,   setSearch]   = useState('');
    const [open,     setOpen]     = useState(false);
    const [active,   setActive]   = useState(0);   // klaviatura ilə seçili sətir
    const containerRef            = useRef(null);
    const inputRef                = useRef(null);
    const listRef                 = useRef(null);

    const selected = options.find(o => String(o.value) === String(value));

    useEffect(() => {
        if (!apiRef) return;
        apiRef.current = { focus: () => { setSearch(''); setOpen(true); setTimeout(() => inputRef.current?.focus(), 0); } };
    }, [apiRef]);

    const filtered = options.filter(o =>
        o.label.toLowerCase().includes(search.toLowerCase())
    );

    // Axtarış/açılış dəyişəndə seçili sətri sıfırla
    useEffect(() => { setActive(0); }, [search, open]);

    // Seçili sətri görünüş sahəsinə sürüşdür
    useEffect(() => {
        if (!open || !listRef.current) return;
        const el = listRef.current.querySelector('[data-active="1"]');
        if (el) el.scrollIntoView({ block: 'nearest' });
    }, [active, open]);

    // Close on outside click
    useEffect(() => {
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const choose = (opt) => {
        if (!opt || opt.disabled) return;
        onChange(String(opt.value));
        setSearch('');
        if (keepOpen) {
            // Ardıcıl əlavə: açıq qal, yenidən fokusla
            setActive(0);
            setTimeout(() => inputRef.current?.focus(), 0);
        } else {
            setOpen(false);
        }
    };

    const clear = (e) => {
        e.stopPropagation();
        onChange('');
        setSearch('');
        setOpen(false);
    };

    const onKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (!open) { setOpen(true); return; }
            setActive((i) => Math.min(i + 1, filtered.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((i) => Math.max(i - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            choose(filtered[active]);
        } else if (e.key === 'Escape') {
            setOpen(false);
            setSearch('');
        }
    };

    // ── Disabled state ────────────────────────────────────────────
    if (disabled) {
        return (
            <div className={`w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-slate-100 text-slate-500 cursor-not-allowed flex items-center justify-between ${className}`}>
                <span className="truncate">{selected ? selected.label : placeholder}</span>
            </div>
        );
    }

    return (
        <div ref={containerRef} className={`relative ${className}`}>

            {/* ── Trigger ── */}
            {selected ? (
                /* Selected: show chip with × */
                <div
                    onClick={() => { setOpen(true); setSearch(''); }}
                    className="w-full border border-blue-300 bg-blue-50 rounded-lg px-3 py-2 text-sm flex items-center gap-2 cursor-pointer hover:border-blue-400 transition-colors"
                >
                    <span className="flex-1 truncate font-medium text-blue-800">{selected.label}</span>
                    <button
                        onClick={clear}
                        className="text-blue-400 hover:text-blue-700 text-base leading-none shrink-0 w-5 h-5 flex items-center justify-center rounded hover:bg-blue-100"
                    >×</button>
                </div>
            ) : (
                /* Not selected: show search input */
                <input
                    ref={inputRef}
                    value={search}
                    onChange={e => { setSearch(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={onKeyDown}
                    placeholder={placeholder}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400 transition-colors bg-white"
                />
            )}

            {/* ── Dropdown ── */}
            {open && (
                <div ref={listRef} className="absolute z-40 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                    {/* Seçili haldakı çip axtarış qutusu göstərmir → dropdown-a axtarış sahəsi qoy */}
                    {selected && (
                        <div className="sticky top-0 bg-white p-1.5 border-b border-slate-100">
                            <input
                                autoFocus
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                onKeyDown={onKeyDown}
                                placeholder="🔍 Axtar..."
                                className="w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm outline-none focus:border-blue-400"
                            />
                        </div>
                    )}
                    {/* Show "clear" option at top when something is selected */}
                    {selected && (
                        <button
                            onClick={clear}
                            className="w-full text-left px-3 py-2 text-xs text-slate-400 hover:bg-slate-50 border-b border-slate-100 italic"
                        >
                            — Seçimi sil —
                        </button>
                    )}
                    {filtered.length === 0 ? (
                        <p className="px-3 py-2 text-xs text-slate-400">Tapılmadı</p>
                    ) : (
                        filtered.map((opt, idx) => (
                            <button
                                key={opt.value}
                                data-active={idx === active ? '1' : '0'}
                                onMouseEnter={() => setActive(idx)}
                                onClick={() => choose(opt)}
                                disabled={opt.disabled}
                                className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 border-b border-slate-50 last:border-0
                                    ${opt.disabled
                                        ? 'text-slate-300 cursor-not-allowed'
                                        : idx === active
                                            ? 'bg-blue-100 text-blue-800 font-semibold'
                                            : String(opt.value) === String(value)
                                                ? 'bg-blue-50 text-blue-700 font-semibold'
                                                : 'text-slate-700 hover:bg-blue-50'}`}
                            >
                                <span className="truncate">{opt.label}</span>
                                {opt.disabled && <span className="ml-auto text-[10px] text-slate-300">🔒</span>}
                            </button>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}
